import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';

export type FixedAssetValue = Tables<'fixed_asset_values'>;

export interface CreateFixedAssetValueInput {
  fixed_asset_id: string;
  value: number;
  recorded_at: string;
  notes?: string;
}

export function useFixedAssetValues(assetId: string | undefined) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['fixed-asset-values', assetId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('fixed_asset_values')
        .select('*')
        .eq('fixed_asset_id', assetId!)
        .order('recorded_at', { ascending: true });
      if (error) throw error;
      return data as FixedAssetValue[];
    },
    enabled: !!assetId,
  });

  const createValue = useMutation({
    mutationFn: async (input: CreateFixedAssetValueInput) => {
      const { data, error } = await supabase
        .from('fixed_asset_values')
        .insert({
          fixed_asset_id: input.fixed_asset_id,
          value: input.value,
          recorded_at: input.recorded_at,
          notes: input.notes ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fixed-asset-values', assetId] });
    },
  });

  return { ...query, createValue };
}
